<template>
  <div>
    <h2 class="text-h5 font-weight-bold mb-4">Convocation Eligible Students</h2>

    <!-- Row: year selector + matric search + Search button -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedYear"
          :items="years"
          item-text="yearLabel"
          item-value="id"
          return-object
          label="Select Convocation Year"
          outlined
          dense
          hide-details
          :loading="loadingYears"
          @change="onYearChange"
        />
      </v-col>
      <v-col cols="12" md="5">
        <v-text-field
          v-model="searchMatric"
          label="Search by Matric Number"
          outlined
          dense
          hide-details
          clearable
          @click:clear="onClearSearch"
          @keyup.enter="runSearch"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          :disabled="!selectedYear"
          :loading="searching"
          @click="runSearch"
        >Search</v-btn>
      </v-col>
    </v-row>

    <!-- Empty state: no active years -->
    <v-alert
      v-if="!loadingYears && years.length === 0"
      type="info"
      outlined
    >
      No active convocation years are available at this time.
    </v-alert>

    <!-- Not found alert -->
    <v-alert
      v-if="notFound"
      type="warning"
      outlined
      dismissible
      @input="notFound = false"
    >
      No eligible student found for <strong>{{ lastSearch }}</strong> in the selected convocation year.
    </v-alert>

    <!-- Records table -->
    <template v-if="selectedYear">
      <v-card v-if="records.length > 0" outlined>
        <v-data-table
          :headers="headers"
          :items="records"
          :loading="loadingRecords"
          hide-default-footer
          disable-sort
        >
          <template #item.actions="{ item }">
            <v-btn
              small
              color="primary"
              @click="payFee(item)"
            >Pay Fee</v-btn>
          </template>
        </v-data-table>

        <div class="d-flex justify-center pa-3" v-if="!searching && totalPages > 1">
          <v-pagination
            v-model="page"
            :length="totalPages"
            @input="browse"
          />
        </div>
      </v-card>

      <!-- Empty browse state -->
      <v-alert
        v-else-if="!loadingRecords && !notFound"
        type="info"
        outlined
      >
        No records found for the selected year.
      </v-alert>
    </template>
  </div>
</template>

<script>
import Convocation from '@/shared/apis/convocation';

export default {
  name: 'ConvocationLandingPage',

  data: () => ({
    years: [],
    loadingYears: false,
    selectedYear: null,
    searchMatric: '',
    lastSearch: '',
    searching: false,
    notFound: false,
    records: [],
    loadingRecords: false,
    page: 1,
    pageSize: 20,
    totalPages: 0,
    headers: [
      { text: 'Matric', value: 'matric' },
      { text: 'Name', value: 'name' },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ],
  }),

  created() {
    this.loadYears();
  },

  methods: {
    loadYears() {
      this.loadingYears = true;
      Convocation.publicYears()
        .then(({ data }) => {
          this.years = data || [];
        })
        .catch(() => {})
        .finally(() => {
          this.loadingYears = false;
        });
    },

    onYearChange() {
      this.page = 1;
      this.searchMatric = '';
      this.lastSearch = '';
      this.notFound = false;
      this.browse();
    },

    browse() {
      if (!this.selectedYear) return;
      this.loadingRecords = true;
      this.notFound = false;
      Convocation.publicRecords(this.selectedYear.id, this.page - 1, this.pageSize)
        .then(({ data }) => {
          this.records = data.content || [];
          this.totalPages = data.totalPages || 0;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingRecords = false;
        });
    },

    runSearch() {
      if (!this.selectedYear || !this.searchMatric.trim()) return;
      this.searching = true;
      this.notFound = false;
      this.lastSearch = this.searchMatric.trim();
      Convocation.searchByMatric(this.selectedYear.id, this.searchMatric.trim(), 0, this.pageSize)
        .then(({ data }) => {
          const results = data.content || [];
          if (results.length === 0) {
            this.notFound = true;
            this.records = [];
            this.totalPages = 0;
          } else {
            this.records = results;
            this.totalPages = data.totalPages || 0;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.searching = false;
        });
    },

    onClearSearch() {
      this.searchMatric = '';
      this.lastSearch = '';
      this.notFound = false;
      this.page = 1;
      this.browse();
    },

    payFee(item) {
      this.$router.push({
        path: '/payment',
        query: { yearId: this.selectedYear.id, yearLabel: this.selectedYear.yearLabel, matric: item.matric },
      });
    },
  },
};
</script>
